import * as amplitude from "@amplitude/analytics-browser";
import { useInput } from "@redotech/react-util/form";
import { useHandler } from "@redotech/react-util/hook";
import {
  getValidDate,
  isValidDate,
  textReminderEligible,
} from "@redotech/redo-customer-portal-shared/shipment-method/util";
import {
  DraftReturn,
  ShipmentMethod,
  ShipmentMethodName,
} from "@redotech/redo-model/draft-return/draft-return";
import { Resource } from "@redotech/redo-model/localization/resource";
import {
  PickupLocation,
  pickupLocationOptions,
} from "@redotech/redo-model/return-flow";
import { PackagePickupVariant } from "@redotech/redo-model/team";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoPhoneNumberInput } from "@redotech/redo-web/arbiter-components/input/redo-phone-number-input";
import FeaturedTruckIcon from "@redotech/redo-web/arbiter-icon/featured-pickup-truck.svg";
import GrayModalCloseButton from "@redotech/redo-web/arbiter-icon/gray-close-x.svg";
import {
  Button,
  ButtonBorder,
  ButtonSize,
  ButtonTheme,
} from "@redotech/redo-web/button";
import { FormCheckbox } from "@redotech/redo-web/checkbox";
import { CurrencyContext } from "@redotech/redo-web/currency";
import { DateInput, MaybeDate } from "@redotech/redo-web/date-picker";
import { Flex } from "@redotech/redo-web/flex";
import { OrSeperator } from "@redotech/redo-web/or-seperator";
import { SelectDropdown } from "@redotech/redo-web/select-dropdown";
import { Text } from "@redotech/redo-web/text";
import { InputLines, TextInput } from "@redotech/redo-web/text-input";
import { isValidPhoneNumber } from "@redotech/redo-web/utils/validators";
import {
  groupInput,
  input,
  InputProvider,
  optionalInput,
} from "@redotech/ui/form";
import { optionalEqual } from "@redotech/util/equal";
import {
  dateToCurrentPlainDate,
  plainDateToCurrentDate,
} from "@redotech/util/temporal";
import {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { getTeamCustomLayout } from "./custom-package-helper";
import { CustomLayout } from "./custom-package-pickup";

const BASE_PICKUP_FEE = 6;

export const PackagePickup = memo(function PackagePickup({
  draftReturn,
  updatePickupVariant,
  pickupFee = BASE_PICKUP_FEE,
  showReturnInStoreOption,
  onCancel,
  onDecline,
  selectMethod,
  nonPickupFee = 0,
  teamId,
  copyResource,
  variants,
  treatmentHash,
  useBetaPackagePickupModal,
}: {
  draftReturn: DraftReturn;
  updatePickupVariant: (pickupVariant: string) => void;
  pickupFee?: number;
  showReturnInStoreOption: boolean;
  onCancel(): void;
  onDecline(): void;
  selectMethod(method: ShipmentMethod): void;
  nonPickupFee?: number;
  teamId: string;
  copyResource: Resource;
  variants?: PackagePickupVariant[];
  treatmentHash: string;
  useBetaPackagePickupModal?: boolean;
}) {
  useEffect(() => {
    amplitude.logEvent("pickup-available", { upsell: pickupFee });
  }, [pickupFee]);

  const [showForm, setShowForm] = useState(false);

  const pickupInput = useInput(pickupForm, {
    finalized: false,
    pickup: undefined,
  });

  const areAnyOrdersProtected = draftReturn.orders.some(
    (order) => order.protected,
  );

  const onChoosePickup = useCallback(() => {
    amplitude.logEvent("pickup-add", { isReviewStep: true });
    pickupInput.setValue({
      finalized: false,
      pickup: {
        location: undefined,
        date: undefined,
        specialInstructions: "",
        textReminder: false,
        phone: "",
      },
    });
    setShowForm(true);
  }, [pickupInput, setShowForm]);

  const onChooseSelfShip = useCallback(() => {
    amplitude.logEvent("pickup-cancel", { isReviewStep: true });
    pickupInput.setValue({ finalized: true, pickup: undefined });
    onDecline();
  }, [pickupInput, onDecline]);

  const onChooseReturnInStore = useCallback(() => {
    pickupInput.setValue({ finalized: true, pickup: undefined });
    selectMethod({ method: ShipmentMethodName.IN_STORE });
  }, [pickupInput, selectMethod]);

  // Handle A/B testing pickup variants
  let selectedVariant: PackagePickupVariant | undefined;
  if (variants && variants.length > 0 && treatmentHash) {
    const hashPercentage = parseInt(treatmentHash, 16) % 100;

    const cumulativePercentages = variants.map((variant, index) => {
      return variants
        .slice(0, index + 1)
        .reduce((sum, b) => sum + b.percentage, 0);
    });

    for (let i = 0; i < cumulativePercentages.length; i++) {
      if (hashPercentage < cumulativePercentages[i]) {
        selectedVariant = variants[i];
        break;
      }
    }
  }

  if (selectedVariant) {
    updatePickupVariant(selectedVariant.variantId);

    amplitude.logEvent("pickup-variant-selected", {
      variantId: selectedVariant.variantId,
    });
  }

  const packagePickupUpsell = useMemo(() => {
    const teamCustomLayout = getTeamCustomLayout(teamId);

    if (teamCustomLayout) {
      return (
        <CustomLayout
          areAnyOrdersProtected={areAnyOrdersProtected}
          nonPickupFee={nonPickupFee}
          onChoosePickup={onChoosePickup}
          onChooseReturnInStore={onChooseReturnInStore}
          onChooseSelfShip={onChooseSelfShip}
          pickupFee={pickupFee}
          pickupModalMainText={copyResource.PICKUP_MODAL_MAIN_TEXT}
          pickupModalPickupButtonText={
            copyResource.PICKUP_MODAL_PICKUP_BUTTON_TEXT
          }
          pickupModalShippingButtonText={
            copyResource.PICKUP_MODAL_SHIPPING_BUTTON_TEXT
          }
          pickupModalSubText={copyResource.PICKUP_MODAL_SUB_TEXT}
          returnInStoreText={copyResource.IN_STORE_RETURN_TEXT}
          showReturnInStoreOption={showReturnInStoreOption}
          teamCustomLayout={teamCustomLayout}
        />
      );
    }

    if (useBetaPackagePickupModal) {
      return (
        <BetaLayout
          areAnyOrdersProtected={areAnyOrdersProtected}
          nonPickupFee={nonPickupFee}
          onChoosePickup={onChoosePickup}
          onChooseReturnInStore={onChooseReturnInStore}
          onChooseSelfShip={onChooseSelfShip}
          pickupFee={pickupFee}
          pickupModalMainText={copyResource.PICKUP_MODAL_MAIN_TEXT}
          pickupModalPickupButtonText={
            copyResource.PICKUP_MODAL_PICKUP_BUTTON_TEXT
          }
          pickupModalShippingButtonText={
            copyResource.PICKUP_MODAL_SHIPPING_BUTTON_TEXT
          }
          pickupModalSubText={copyResource.PICKUP_MODAL_SUB_TEXT}
          returnInStoreText={copyResource.IN_STORE_RETURN_TEXT}
          showReturnInStoreOption={showReturnInStoreOption}
        />
      );
    }

    return (
      <DefaultLayout
        onChoosePickup={onChoosePickup}
        onChooseReturnInStore={onChooseReturnInStore}
        onChooseSelfShip={onChooseSelfShip}
        pickupFee={pickupFee}
        pickupModalMainText={
          selectedVariant?.mainText ?? copyResource.PICKUP_MODAL_MAIN_TEXT
        }
        pickupModalPickupButtonText={
          selectedVariant?.pickupButtonText ??
          copyResource.PICKUP_MODAL_PICKUP_BUTTON_TEXT
        }
        pickupModalShippingButtonText={
          selectedVariant?.shippingButtonText ??
          copyResource.PICKUP_MODAL_SHIPPING_BUTTON_TEXT
        }
        pickupModalSubText={
          selectedVariant?.subText ?? copyResource.PICKUP_MODAL_SUB_TEXT
        }
        returnInStoreText={
          selectedVariant?.returnInStoreText ??
          copyResource.IN_STORE_RETURN_TEXT
        }
        showReturnInStoreOption={showReturnInStoreOption}
      />
    );
  }, [
    teamId,
    areAnyOrdersProtected,
    useBetaPackagePickupModal,
    onChoosePickup,
    onChooseReturnInStore,
    onChooseSelfShip,
    showReturnInStoreOption,
    pickupFee,
    nonPickupFee,
    selectedVariant,
    copyResource.IN_STORE_RETURN_TEXT,
    copyResource.PICKUP_MODAL_MAIN_TEXT,
    copyResource.PICKUP_MODAL_SUB_TEXT,
    copyResource.PICKUP_MODAL_PICKUP_BUTTON_TEXT,
    copyResource.PICKUP_MODAL_SHIPPING_BUTTON_TEXT,
  ]);

  return (
    <>
      <Flex align="center" justify="space-between">
        <FeaturedTruckIcon height={52} width={52} />
        <GrayModalCloseButton
          onClick={() => (showForm ? setShowForm(false) : onCancel())}
          style={{ cursor: "pointer" }}
        />
      </Flex>
      {!showForm ? (
        packagePickupUpsell
      ) : (
        <PickupFormCard
          input={pickupInput}
          isReviewStep
          labelDeducted={false}
          pickupFee={pickupFee}
          selectMethod={selectMethod}
        />
      )}
    </>
  );
});

const DefaultLayout = memo(function DefaultLayout({
  pickupModalMainText,
  pickupModalSubText,
  pickupModalPickupButtonText,
  pickupModalShippingButtonText,
  onChoosePickup,
  onChooseReturnInStore,
  onChooseSelfShip,
  showReturnInStoreOption,
  pickupFee,
  returnInStoreText,
}: {
  pickupModalMainText: string;
  pickupModalSubText: string;
  pickupModalPickupButtonText: string;
  pickupModalShippingButtonText: string;
  onChoosePickup(): void;
  onChooseReturnInStore(): void;
  onChooseSelfShip(): void;
  showReturnInStoreOption: boolean;
  pickupFee: number;
  returnInStoreText: string;
}) {
  const { formatCurrencyFromUSD } = useContext(CurrencyContext);

  return (
    <Flex dir="column" gap="4xl">
      <Flex dir="column" gap="xxs">
        <Text fontSize="lg" fontWeight="semibold">
          {pickupModalMainText}
        </Text>
        <Text fontSize="sm" fontWeight="thin" textColor="tertiary">
          {pickupModalSubText.replaceAll(
            "%price%",
            formatCurrencyFromUSD(pickupFee),
          )}
        </Text>
      </Flex>
      <Flex dir="column" gap="lg">
        <Button
          border={ButtonBorder.LIGHT}
          onClick={onChoosePickup}
          size={ButtonSize.LARGE}
          theme={ButtonTheme.PRIMARY}
        >
          {pickupModalPickupButtonText}
        </Button>
        <Flex dir="row" gap="lg" wrap="wrap">
          <Button
            border={ButtonBorder.LIGHT}
            onClick={onChooseSelfShip}
            size={ButtonSize.LARGE}
            style={{ flex: 1 }}
            theme={ButtonTheme.OUTLINED}
          >
            {pickupModalShippingButtonText}
          </Button>
          {showReturnInStoreOption && (
            <Button
              border={ButtonBorder.LIGHT}
              onClick={onChooseReturnInStore}
              size={ButtonSize.LARGE}
              style={{ flex: 1 }}
              theme={ButtonTheme.OUTLINED}
            >
              {returnInStoreText}
            </Button>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
});

// Subtext not being used, waiting for dynamic content from Maxwell
// This layout is being used as a Beta for certain merchants
/** @see https://linear.app/redo/issue/RETCO-822/recreate-whiskey-skies-and-resort-to-style-pickup-modals */
const BetaLayout = memo(function BetaLayout({
  pickupModalMainText,
  pickupModalSubText,
  pickupModalPickupButtonText,
  pickupModalShippingButtonText,
  areAnyOrdersProtected,
  onChoosePickup,
  onChooseReturnInStore,
  onChooseSelfShip,
  showReturnInStoreOption,
  pickupFee,
  nonPickupFee,
  returnInStoreText,
}: {
  pickupModalMainText: string;
  pickupModalSubText: string;
  pickupModalPickupButtonText: string;
  pickupModalShippingButtonText: string;
  areAnyOrdersProtected: boolean;
  onChoosePickup(): void;
  onChooseReturnInStore(): void;
  onChooseSelfShip(): void;
  showReturnInStoreOption: boolean;
  pickupFee: number;
  nonPickupFee: number;
  returnInStoreText: string;
}) {
  const { formatCurrencyFromUSD } = useContext(CurrencyContext);

  return (
    <Flex dir="column" gap="4xl">
      <Flex dir="column" gap="xxs">
        <Text fontSize="lg" fontWeight="semibold">
          {pickupModalMainText}
        </Text>
        <Text fontSize="sm" fontWeight="thin" textColor="tertiary">
          We get it, life gets busy. For {formatCurrencyFromUSD(pickupFee)} we
          can pick up your package at your door.{" "}
          {areAnyOrdersProtected
            ? "Or you can drop it off with a carrier at no additional cost."
            : "Or you can drop it off with a carrier and only pay for the cost of the label."}
        </Text>
      </Flex>
      <Flex dir="column" gap="lg">
        <Button
          border={ButtonBorder.LIGHT}
          onClick={onChoosePickup}
          size={ButtonSize.LARGE}
          theme={ButtonTheme.PRIMARY}
        >
          {pickupModalPickupButtonText}
        </Button>
        <OrSeperator />
        <Flex dir="row" gap="lg" wrap="wrap">
          <Button
            border={ButtonBorder.LIGHT}
            onClick={onChooseSelfShip}
            size={ButtonSize.LARGE}
            style={{ flex: 1 }}
            theme={ButtonTheme.OUTLINED}
          >
            {pickupModalShippingButtonText}
          </Button>
          {showReturnInStoreOption && (
            <Button
              border={ButtonBorder.LIGHT}
              onClick={onChooseReturnInStore}
              size={ButtonSize.LARGE}
              style={{ flex: 1 }}
              theme={ButtonTheme.OUTLINED}
            >
              {returnInStoreText}
            </Button>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
});

const pickupDetailsForm = groupInput(
  {
    location: input<PickupLocation | undefined>({
      validator: (location) => (location ? [] : ["Required"]),
    }),
    date: input<Temporal.PlainDate | undefined>({
      equal: optionalEqual((a, b) => a.equals(b)),
      validator: (date) => (date !== undefined ? [] : ["Required"]),
    }),
    specialInstructions: input<string>(),
    textReminder: input<boolean>(),
    phone: input<string>({
      validator: (phone) => {
        if (!phone) {
          return ["Phone number is required"];
        }
        if (!isValidPhoneNumber(phone)) {
          return ["Please enter a valid phone number"];
        }
        return [];
      },
    }),
  },
  {
    validator: ({ location, specialInstructions }) =>
      location !== PickupLocation.OTHER || specialInstructions
        ? []
        : ["Instructions required"],
  },
);

export const pickupForm = groupInput({
  finalized: input<boolean>({
    validator: (value) => (value ? [] : ["Required"]),
  }),
  pickup: optionalInput(pickupDetailsForm, () => ({
    location: undefined,
    date: undefined,
    specialInstructions: "",
    textReminder: false,
    phone: "",
  })),
});
type PickupForm = InputProvider.Form<typeof pickupForm>;

const PickupFormCard = memo(function PickupFormCard({
  pickupFee,
  labelDeducted,
  selectMethod,
  setLoading,
  isReviewStep,
  isMerchantDashboard,
  isStatusPage,
  input,
  pending,
  setPending,
  errorMessage,
}: {
  pickupFee?: number | undefined;
  labelDeducted?: boolean;
  selectMethod: (method: ShipmentMethod) => void;
  setLoading?: (loading?: boolean) => void;
  isReviewStep?: boolean;
  isMerchantDashboard?: boolean;
  isStatusPage?: boolean;
  input: PickupForm;
  pending?: boolean;
  setPending?: (pending: boolean) => void;
  errorMessage?: string;
}) {
  const { formatCurrency } = useContext(CurrencyContext);
  const today = useMemo(() => Temporal.Now.plainDateISO(), []);

  const minDate = getValidDate(today, 1);
  // FedEx can be scheduled up to 14 days in advance
  const maxDate = getValidDate(today, 14);

  const { finalized, pickup } = input.inputs;
  const { date, location, specialInstructions, textReminder, phone } =
    pickup.input?.inputs ?? {};
  const [phoneInputChanged, setPhoneInputChanged] = useState<boolean>(false);

  // Memoize the phone value setter to prevent re-renders
  const handlePhoneChange = useHandler((val: string) => {
    setPhoneInputChanged(true);
    phone?.setValue(val);
  });

  const phoneInputIsError =
    phoneInputChanged && phone?.allErrors && phone?.allErrors?.length > 0;

  useEffect(() => {
    if (pickup.value?.date === undefined) {
      date?.setValue(minDate);
    }
    if (pickup.value?.location === undefined) {
      location?.setValue(PickupLocation.FRONT_DOOR);
    }
  }, [date, location, minDate, pickup.value?.date, pickup.value?.location]);

  const savePickup = useHandler(async () => {
    if (!isMerchantDashboard && !isStatusPage) {
      amplitude.logEvent("pickup-saved", {
        date: date?.toString(),
        location: location?.value,
        specialInstructions: specialInstructions?.value,
        textReminder: pickup.value?.textReminder,
        phone: phone?.value,
      });
    }
    setLoading?.(true);
    setPending?.(true);
    finalized.setValue(true);
    selectMethod({
      method: ShipmentMethodName.PICKUP,
      pickupData: {
        pickupDate: date?.value?.toString() ?? "",
        pickupLocation: {
          packageLocation: location?.value ?? PickupLocation.FRONT_DOOR,
          specialInstructions: specialInstructions?.value,
        },
        textReminder: pickup.value?.textReminder ?? false,
        phone: phone?.value ?? "",
      },
    });
  });

  return (
    <>
      {!isMerchantDashboard && (
        <Text fontSize="md">
          Your package will be picked up from the pickup address on{" "}
          {isReviewStep ? "the next page." : "this page."}{" "}
          {pickupFee &&
            (labelDeducted ? (
              <>
                An additional charge of{" "}
                <Text as="span" fontWeight="bold">
                  {formatCurrency(pickupFee)}
                </Text>{" "}
                will be deducted from your refund.
              </>
            ) : (
              <>
                You will be charged an additional charge of{" "}
                <Text as="span" fontWeight="bold">
                  {formatCurrency(pickupFee)}
                </Text>
                .
              </>
            ))}
        </Text>
      )}
      <Text fontSize="sm" fontWeight="bold">
        Pickup details:
      </Text>
      <DateInput
        error={date?.allErrors && date?.allErrors?.length > 0}
        filterDate={({ date }) => {
          return isValidDate(dateToCurrentPlainDate(date));
        }}
        includeYearNav={false}
        maxDate={plainDateToCurrentDate(maxDate)}
        minDate={plainDateToCurrentDate(minDate)}
        minDetail="year"
        value={
          (date?.value
            ? plainDateToCurrentDate(date?.value)
            : undefined) as MaybeDate
        }
        valueChange={(value) => {
          if (value && isValidDate(dateToCurrentPlainDate(value))) {
            date?.setValue(dateToCurrentPlainDate(value));
          } else {
            date?.setValue(undefined);
          }
        }}
      />
      {date?.allErrors && date?.allErrors?.length > 0 && (
        <Text textColor="error">{date?.allErrors[0]}</Text>
      )}
      <SelectDropdown
        options={Array.from(pickupLocationOptions.keys())}
        value={location?.value}
        valueChange={(value) => location?.setValue(value)}
      >
        {(option) => pickupLocationOptions?.get(option)?.label}
      </SelectDropdown>
      <Text fontSize="sm" mt="xxs" textColor="quaternary">
        If {isMerchantDashboard ? "the customer lives" : "you live"} in a
        multi-story building, select an option which is ground level and secure.
      </Text>
      {location?.value === PickupLocation.OTHER && (
        <TextInput
          fullwidth
          lines={InputLines.MULTI}
          maxLength={200}
          onChange={(val) => specialInstructions?.setValue(val)}
          placeholder="Special instructions"
          value={specialInstructions?.value ?? ""}
        />
      )}

      <RedoPhoneNumberInput
        description="This phone number will be used to contact you about your package pickup"
        label="Phone number for pickup"
        required
        setValue={handlePhoneChange}
        size="sm"
        value={phone?.value ?? ""}
      />
      {phoneInputIsError && (
        <Text textColor="error">{phone?.allErrors[0]}</Text>
      )}
      {errorMessage && <Text textColor="error">{errorMessage}</Text>}
      {!isMerchantDashboard && textReminderEligible(date?.value) && (
        <Flex dir="row" gap="xs">
          <FormCheckbox input={textReminder!} />
          Send me text reminders and updates about my pickup.
        </Flex>
      )}
      {!isMerchantDashboard && (
        <RedoButton
          disabled={pickup.allErrors.length > 0}
          hierarchy="primary"
          onClick={savePickup}
          pending={pending}
          size="lg"
          text={
            isStatusPage ? "Reschedule package pickup" : "Add package pickup"
          }
        />
      )}
    </>
  );
});
